import React from 'react';
import cn from 'classnames';
import { subject } from '@casl/ability';
import { Button, BUTTON_TYPES, Tooltip } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { Status } from '_atoms/Status';
import { Priority } from '_atoms/Priority';
import type { PriorityValue } from '_atoms/Priority';
import { BUTTON_TEXT } from 'constants/text';
import { OPENING_STATUS_TYPES, OPENING_STATUSES } from 'constants/opening';
import { ACTIONS, Can, UI } from 'permission';
import type { DashboardSelectOption } from '_templates/dashboardTypes';

type OnOpeningAction = (data: Record<string, unknown>, type: string) => void;

export const COLUMNS_OPENINGS_SERVICE_TOP = [
  {
    title: 'Job',
    sorter: true,
    dataIndex: 'job',
    rowKey: 'job',
    width: 160,
    render: () => null,
  },
  {
    title: 'Level',
    sorter: true,
    dataIndex: 'level',
    rowKey: 'level',
    width: 84,
    render: () => null,
  },
  {
    title: 'Location',
    sorter: true,
    dataIndex: 'location',
    rowKey: 'location',
    width: 120,
    render: () => null,
  },
  {
    title: 'Creation date',
    dataIndex: 'creation_date',
    rowKey: 'creation_date',
    width: 112,
    sorter: true,
    render: () => null,
  },
  {
    title: 'Expected date',
    dataIndex: 'expected_date',
    rowKey: 'expected_date',
    width: 112,
    sorter: true,
    render: () => null,
  },
  {
    title: 'Team lead',
    dataIndex: 'team_lead',
    rowKey: 'team_lead',
    width: 184,
    sorter: true,
    render: () => null,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    rowKey: 'status',
    width: 164,
    sorter: true,
    render: () => null,
  },
  {
    title: 'Required action',
    dataIndex: 'action',
    rowKey: 'action',
    width: 208,
    fixed: 'right',
    render: () => null,
  },
];

export const COLUMNS_OPENINGS_SERVICE = (onAction: OnOpeningAction) => [
  {
    title: 'Job',
    dataIndex: 'job',
    rowKey: 'job',
    width: 160,
    ellipsis: true,
    className: 'all-openings__job',
    render: (data: Record<string, unknown>) => (
      <a href={`/jobs/${data.job_uuid}/show/openings`} target="_blank" rel="noreferrer noopener">
        <Priority priority={data.priority as PriorityValue} />
        <Tooltip label={data.job_name as string}>
          <Typography.Text weight={TYPOGRAPHY_WEIGHT.BOLD}>{data.job_name as string}</Typography.Text>
        </Tooltip>
      </a>
    ),
  },
  {
    title: 'Level',
    dataIndex: 'level',
    rowKey: 'level',
    width: 84,
    className: 'all-openings__level',
    render: (data: string) => <Typography.Text weight={TYPOGRAPHY_WEIGHT.BOLD}>{data}</Typography.Text>,
  },
  {
    title: 'Location',
    dataIndex: 'location',
    rowKey: 'location',
    width: 120,
    className: 'all-openings__location',
    render: (data: string) => (
      <Tooltip label={data}>
        <Typography.Text weight={TYPOGRAPHY_WEIGHT.BOLD}>{data}</Typography.Text>
      </Tooltip>
    ),
  },
  {
    title: 'Creation date',
    dataIndex: 'creation_date',
    rowKey: 'creation_date',
    width: 112,
    className: 'all-openings__creation-date',
    render: (data: string) => <Typography.Text weight={TYPOGRAPHY_WEIGHT.BOLD}>{data}</Typography.Text>,
  },
  {
    title: 'Expected date',
    dataIndex: 'expected_date',
    rowKey: 'expected_date',
    width: 112,
    className: 'all-openings__expected-date',
    render: ({ date, error }: { date: string; error: boolean }) => (
      <Typography.Text className={cn({ error })} weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {date}
      </Typography.Text>
    ),
  },
  {
    title: 'Team lead',
    dataIndex: 'team_lead',
    rowKey: 'team_lead',
    width: 184,
    className: 'all-openings__team-lead',
    render: (data: string) => (
      <Tooltip label={data}>
        <Typography.Text weight={TYPOGRAPHY_WEIGHT.BOLD}>{data}</Typography.Text>
      </Tooltip>
    ),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    rowKey: 'status',
    width: 164,
    className: 'all-openings__status',
    render: (status: string) => <Status type={OPENING_STATUS_TYPES[status]}>{status}</Status>,
  },
  {
    title: 'Required action',
    dataIndex: 'action',
    rowKey: 'action',
    width: 208,
    fixed: 'right',
    hideMobileTitle: true,
    className: 'all-openings__action',
    render: ({ status, ...data }: Record<string, unknown>) => (
      <Can I={ACTIONS.READ} a={subject(UI.APPROVE_DECLINE, { status: status as string }) as never}>
        <Button onClick={() => onAction(data, 'decline')}>{BUTTON_TEXT.DECLINE}</Button>
        <Button onClick={() => onAction(data, 'approve')} type={BUTTON_TYPES.PRIMARY}>
          {BUTTON_TEXT.APPROVE}
        </Button>
      </Can>
    ),
  },
];

export const COLUMNS_OPENINGS_JOB_TOP = [
  {
    title: 'Level',
    sorter: true,
    dataIndex: 'level',
    rowKey: 'level',
    width: 120,
    render: () => null,
  },
  {
    title: 'Location',
    dataIndex: 'location',
    rowKey: 'location',
    width: 200,
    sorter: true,
    render: () => null,
  },
  {
    title: 'Creation date',
    dataIndex: 'creation_date',
    rowKey: 'creation_date',
    width: 138,
    sorter: true,
    render: () => null,
  },
  {
    title: 'Expected date',
    dataIndex: 'expected_date',
    rowKey: 'expected_date',
    width: 138,
    sorter: true,
    render: () => null,
  },
  {
    title: 'Team lead',
    dataIndex: 'team_lead',
    rowKey: 'team_lead',
    width: 174,
    sorter: true,
    render: () => null,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    rowKey: 'status',
    width: 164,
    sorter: true,
    render: () => null,
  },
  {
    title: 'Required action',
    dataIndex: 'action',
    rowKey: 'action',
    width: 210,
    fixed: 'right',
    render: () => null,
  },
];

export const COLUMNS_OPENINGS_JOB = (onAction: OnOpeningAction) => [
  {
    title: 'Level',
    dataIndex: 'level',
    rowKey: 'level',
    width: 120,
    ellipsis: true,
    className: 'all-openings__level',
    render: ({ level, priority }: { level: string; priority: string }) => (
      <>
        <Priority priority={priority as PriorityValue} />
        <Tooltip label={level}>
          <Typography.Text weight={TYPOGRAPHY_WEIGHT.BOLD}>{level}</Typography.Text>
        </Tooltip>
      </>
    ),
  },
  {
    title: 'Location',
    dataIndex: 'location',
    rowKey: 'location',
    width: 200,
    ellipsis: true,
    className: 'all-openings__location',
    render: (data: string) => (
      <Tooltip label={data}>
        <Typography.Text weight={TYPOGRAPHY_WEIGHT.BOLD}>{data}</Typography.Text>
      </Tooltip>
    ),
  },
  {
    title: 'Creation date',
    dataIndex: 'creation_date',
    rowKey: 'creation_date',
    width: 138,
    className: 'all-openings__creation-date',
    render: (data: string) => <Typography.Text weight={TYPOGRAPHY_WEIGHT.BOLD}>{data}</Typography.Text>,
  },
  {
    title: 'Expected date',
    dataIndex: 'expected_date',
    rowKey: 'expected_date',
    width: 138,
    className: 'all-openings__expected-date',
    render: ({ date, error }: { date: string; error: boolean }) => (
      <Typography.Text className={cn({ error })} weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {date}
      </Typography.Text>
    ),
  },
  {
    title: 'Team lead',
    dataIndex: 'team_lead',
    rowKey: 'team_lead',
    width: 174,
    ellipsis: true,
    className: 'all-openings__team-lead',
    render: (data: string) => <Typography.Text weight={TYPOGRAPHY_WEIGHT.BOLD}>{data}</Typography.Text>,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    rowKey: 'status',
    width: 164,
    className: 'all-openings__status',
    render: (status: string) => <Status type={OPENING_STATUS_TYPES[status]}>{status}</Status>,
  },
  {
    title: 'Required action',
    dataIndex: 'action',
    rowKey: 'action',
    width: 210,
    ellipsis: true,
    fixed: 'right',
    hideMobileTitle: true,
    className: 'all-openings__action',
    render: ({ status, ...data }: Record<string, unknown>) => (
      <Can I={ACTIONS.READ} a={subject(UI.APPROVE_DECLINE, { status: status as string }) as never}>
        <Button onClick={() => onAction(data, 'decline')}>{BUTTON_TEXT.DECLINE}</Button>
        <Button onClick={() => onAction(data, 'approve')} type={BUTTON_TYPES.PRIMARY}>
          {BUTTON_TEXT.APPROVE}
        </Button>
      </Can>
    ),
  },
];

export const OPENINGS_GROUP = [
  { value: 'service', label: 'Service', tableData: 'job' },
  { value: 'job', label: 'Job', tableData: 'service' },
];

export const INITIAL_VALUES_1 = {
  limit: { value: 30, label: 30 },
  page: 1,
  offset: 0,
  itemsCount: 0,
};

export const INITIAL_VALUES_2 = {
  limit: { value: 30, label: 30 },
  page: 1,
  offset: 0,
  itemsCount: 0,
};

export const STATUSES: DashboardSelectOption[] = [
  OPENING_STATUSES.SH_APPROVAL,
  OPENING_STATUSES.RM_APPROVAL,
  OPENING_STATUSES.LM_APPROVAL,
  OPENING_STATUSES.HRL_APPROVAL,
  OPENING_STATUSES.OPEN,
  OPENING_STATUSES.WAITING_FOR_ENTRY,
  OPENING_STATUSES.ON_HOLD,
  OPENING_STATUSES.RM_REAPPROVAL,
  OPENING_STATUSES.LM_REAPPROVAL,
].map(value => ({ value, label: value }));
